import { formatDistanceToNow } from 'date-fns';
import { AlertTriangle, Check, Clock, X } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from 'v5/design-system/Badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from 'v5/design-system/Collapsible';
import { cn } from 'v5/platform/dom/cn';
import { VisitorVendorState } from '../../../../api/visitor-vendor-state/visitor-vendor-state.api';
import { JsonDiffViewer } from '../../vendor-state/components/JsonDiffViewer';
import { useSyncStatus } from '../../vendor-state/hooks/useSyncStatus';
import { getVendorStatus } from '../../vendor-state/utils';

interface VendorIntegrationDetailsProps {
  vendorState: VisitorVendorState;
  vendorId: string;
}

/**
 * Detailed view of vendor integration status with expandable sections.
 * Shows drift detection, sync history, and error information.
 */
export const VendorIntegrationDetails: React.FC<
  VendorIntegrationDetailsProps
> = ({ vendorState, vendorId }) => {
  const { t } = useTranslation('visitor-management');
  const syncData = useSyncStatus(vendorState);

  return (
    <div className="space-y-4 pt-4">
      {/* Snapshot Error Section */}
      {vendorState.snapshot_error && (
        <div
          className={cn(
            'rounded-lg p-4 border',
            vendorState.snapshot_error.status === 'preparation_error'
              ? 'bg-red-50 border-red-200'
              : 'bg-gray-50 border-gray-200'
          )}
        >
          <div className="flex items-center gap-2 mb-2">
            {vendorState.snapshot_error.status === 'preparation_error' ? (
              <>
                <X className="w-4 h-4 text-red-600" />
                <h5 className="font-semibold text-red-800 text-sm">
                  Missing Required Data
                </h5>
              </>
            ) : (
              <>
                <span className="text-gray-600 text-sm">ℹ️</span>
                <h5 className="font-semibold text-gray-800 text-sm">
                  Not Eligible for Sync
                </h5>
              </>
            )}
          </div>

          <div className="space-y-2">
            {vendorState.snapshot_error.status === 'preparation_error' ? (
              <>
                {vendorState.snapshot_error.preparation_error_details && (
                  <div>
                    <span className="text-xs font-medium text-gray-600">
                      Missing Field:
                    </span>
                    <div className="text-xs text-gray-800 bg-rose-100 rounded p-1 font-mono mt-1">
                      {
                        vendorState.snapshot_error.preparation_error_details
                          .field
                      }
                    </div>
                  </div>
                )}

                {vendorState.snapshot_error.message && (
                  <div>
                    <span className="text-xs font-medium text-gray-600">
                      Error:
                    </span>
                    <div className="text-xs text-red-700 mt-1">
                      {vendorState.snapshot_error.message}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div>
                  <span className="text-xs font-medium text-gray-600">
                    Reason:
                  </span>
                  <div className="text-xs text-gray-800 bg-white rounded p-1 font-mono mt-1">
                    {vendorState.snapshot_error.reason}
                  </div>
                </div>

                {vendorState.snapshot_error.message && (
                  <div>
                    <span className="text-xs font-medium text-gray-600">
                      Details:
                    </span>
                    <div className="text-xs text-gray-700 mt-1">
                      {vendorState.snapshot_error.message}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Drift Detection Section */}
      {!vendorState.snapshot_error && vendorState.in_sync === false && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <X className="w-4 h-4 text-amber-600" />
            <h5 className="font-semibold text-amber-800 text-sm">
              Data Drift Detected
            </h5>
          </div>

          <p className="text-xs text-amber-700 mb-3">
            Current booking data differs from what the vendor knows.
          </p>

          <JsonDiffViewer
            current={vendorState.current_snapshot}
            stored={vendorState.snapshot}
            title="What Changed"
            maxHeight="max-h-64"
          />
        </div>
      )}

      {/* In Sync Section */}
      {!vendorState.snapshot_error && vendorState.in_sync === true && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Check className="w-4 h-4 text-green-600" />
            <h5 className="font-semibold text-green-800 text-sm">
              Data In Sync
            </h5>
          </div>

          <p className="text-xs text-green-700 mb-3">
            Vendor data matches current booking information.
          </p>

          {vendorState.snapshot ? (
            <div>
              <span className="text-xs font-medium text-gray-600">
                Current Snapshot:
              </span>
              <pre className="text-xs bg-gray-100 rounded p-2 mt-1 overflow-x-auto">
                {JSON.stringify(vendorState.snapshot as any, null, 2)}
              </pre>
            </div>
          ) : null}
        </div>
      )}

      {/* Sync History Section */}
      {syncData && (
        <Collapsible>
          <CollapsibleTrigger className="flex items-center w-full gap-4 justify-between px-4 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
            <div className="flex items-center gap-2">
              {syncData.status === 'success' ? (
                <Check className="w-4 h-4 text-green-600" />
              ) : (
                <X className="w-4 h-4 text-red-600" />
              )}
              <span className="font-medium text-sm">{syncData.title}</span>
            </div>
            <div className="ml-auto flex items-center gap-1 text-xs text-gray-600">
              <Clock className="w-3 h-3" />
              {formatDistanceToNow(new Date(syncData.timestamp!), {
                addSuffix: true
              })}
            </div>
          </CollapsibleTrigger>

          <CollapsibleContent>
            <div className="mt-3 space-y-3">
              {syncData.error && (
                <div className="bg-red-50 border border-red-200 rounded p-3">
                  <h6 className="font-medium text-red-800 text-xs mb-1">
                    Error Message
                  </h6>
                  <p className="text-xs text-red-700">{syncData.error}</p>
                </div>
              )}

              {syncData.request && (
                <div>
                  <h6 className="font-medium text-gray-800 text-xs mb-1">
                    Request
                  </h6>
                  <pre className="bg-gray-100 border border-gray-200 rounded p-2 text-xs font-mono overflow-x-auto">
                    {JSON.stringify(syncData.request, null, 2)}
                  </pre>
                </div>
              )}

              {syncData.response && (
                <div>
                  <h6 className="font-medium text-gray-800 text-xs mb-1">
                    Response
                  </h6>
                  <pre className="bg-gray-100 border border-gray-200 rounded p-2 text-xs font-mono overflow-x-auto">
                    {JSON.stringify(syncData.response, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Registration Status */}
      <div
        className={cn(
          'flex items-center justify-between p-3 rounded-lg',
          getVendorStatus(vendorState).type === 'drift' && vendorState.snapshot
            ? 'bg-amber-50 border border-amber-200'
            : 'bg-gray-50'
        )}
      >
        <span className="text-sm font-medium text-gray-700">
          Registration Status:
        </span>
        <div className="flex items-center gap-2">
          <Badge
            variant={vendorState.snapshot ? 'success' : 'gray'}
            tone="subtle"
            size="sm"
          >
            {vendorState.snapshot ? 'Registered' : 'Not Registered'}
          </Badge>
          {(() => {
            const isDrift = getVendorStatus(vendorState).type === 'drift';
            const isRegistered = !!vendorState.snapshot;
            return isDrift && isRegistered ? (
              <Badge
                variant="attention"
                tone="solid"
                size="sm"
                className="gap-1"
              >
                <AlertTriangle className="w-3 h-3" />
                <span className="text-xs">Needs Unregister</span>
              </Badge>
            ) : null;
          })()}
        </div>
      </div>
    </div>
  );
};
